import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { useIntl } from '../../util/reactIntl';
import { useSdk } from '../../sdkContext';
import { fetchProductDetails, fetchAvailabilityTimeSlots } from './api';

import {
  Page,
  UserNav,
  LayoutSideNavigation,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperBusinessSideNav,
} from '../../components';
import { TopbarContainer } from '../../containers';
import StockAvailabilityCalendar from './StockAvailabilityCalendar';
import css from './ManageStockAvailabilityPage.css';

// Helper to format a local JS Date directly to 'YYYY-MM-DD' string for Sharetribe SDK
const formatToYMD = (dateObj) => {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ManageStockAvailabilityPage = () => {
  const { listingId, providerId } = useParams();
  const history = useHistory();
  const sdk = useSdk();
  const intl = useIntl();

  const user = useSelector(state => state.user);
  const currentUser = user?.currentUser;
  const currentUserProviders = user?.userProviders || [];

  const [currentDate, setCurrentDate] = useState(() => {
    const now = new Date();
    // Default to the 1st day of the current local month
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  // Find currently selected provider object from user's provider list
  const currentProvider = useMemo(() => {
    return currentUserProviders.find(p => (p.id?.uuid || p.id) === providerId);
  }, [currentUserProviders, providerId]);

  // Calculate permissions dynamically for the currently selected provider
  const userPermissions = useMemo(() => {
    if (!currentUser) {
      return { isAdmin: false, isOwner: false, isEmployee: false, roleLabel: 'Brak roli' };
    }

    const { isAdmin = false, adminForCountries = [] } = currentUser.attributes || {};
    const providerCountry =
      currentProvider?.attributes?.country ||
      currentProvider?.attributes?.address?.country;

    const isCountryAdmin =
      Array.isArray(adminForCountries) &&
      providerCountry &&
      adminForCountries.includes(providerCountry);

    const isEffectiveAdmin = Boolean(isAdmin || isCountryAdmin);

    if (isEffectiveAdmin) {
      return {
        isAdmin: true,
        isOwner: true,
        isEmployee: true,
        roleLabel: isAdmin ? 'Admin' : `Admin (${providerCountry?.toUpperCase()})`,
      };
    }

    const myRole = currentProvider?.attributes?.myRole;
    const isOwner = myRole === 'owner';
    const isEmployee = myRole === 'employee' || isOwner;

    return {
      isAdmin: false,
      isOwner,
      isEmployee,
      roleLabel: isOwner ? 'Właściciel (Owner)' : isEmployee ? 'Pracownik (Employee)' : 'Brak roli',
    };
  }, [currentUser, currentProvider]);

  // Fetch product listing details
  const {
    data: productResponse,
    isLoading: isProductLoading,
    isError: isProductError,
  } = useQuery({
    queryKey: ['productDetails', listingId],
    queryFn: () => fetchProductDetails(sdk, listingId),
    enabled: Boolean(listingId),
    staleTime: 1000 * 60 * 5,
  });

  const product = useMemo(() => {
    return productResponse?.data?.data || productResponse?.data || null;
  }, [productResponse]);

  const productTitle = product?.attributes?.title || 'Produkt';

  // Calculate strict API date boundaries using local time, formatted strictly to YYYY-MM-DD
  const { startOfCurrentMonth, endOfCurrentMonth } = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // Sharetribe expects exclusive end date (1st of next month)
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 1);
    
    return {
      startOfCurrentMonth: formatToYMD(start),
      endOfCurrentMonth: formatToYMD(end),
    };
  }, [currentDate]);

  // Fetch availability time slots passing strict YYYY-MM-DD stringified dates
  const {
    data: timeSlotsResponse,
    isLoading: isTimeSlotsLoading,
    isFetching: isTimeSlotsFetching,
    isError: isTimeSlotsError,
  } = useQuery({
    queryKey: ['timeSlots', listingId, startOfCurrentMonth, endOfCurrentMonth],
    queryFn: () =>
      fetchAvailabilityTimeSlots(sdk, {
        listingId,
        start: startOfCurrentMonth,
        end: endOfCurrentMonth,
      }),
    enabled: Boolean(listingId),
    staleTime: 1000 * 60 * 2,
  });

  // Navigation handler
  const handleBackClick = () => {
    if (providerId) {
      history.push(`/stock/${providerId}`);
    } else {
      history.goBack();
    }
  };

  return (
    <Page title={`Dostępność - ${productTitle}`}>
      <LayoutSideNavigation>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="ManageListingsPage" />
          <UserNav selectedPageName="ManageListingsPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperBusinessSideNav currentTab="ManageListingsPage" />
        <LayoutWrapperMain>
          {/* Display active store role badge */}
          <div className={css.roleBadgeContainer}>
            <span>Rola dla wybranego sklepu:</span>
            <strong className={css.roleBadgeText}>{userPermissions.roleLabel}</strong>
          </div>

          {/* Navigation header row */}
          <div className={css.topHeaderRow}>
            <button type="button" className={css.backBtn} onClick={handleBackClick}>
              ← Powrót do listy produktów
            </button>
          </div>

          {/* Product details display section */}
          {isProductLoading ? (
            <div className={css.loadingInsideLayout}>
              <div className={css.spinner} />
              <span>Ładowanie danych produktu...</span>
            </div>
          ) : isProductError ? (
            <div className={css.errorInsideLayout}>
              <span>Wystąpił błąd podczas pobierania informacji o produkcie. Spróbuj ponownie.</span>
            </div>
          ) : (
            <div className={css.productCard}>
              <h1 className={css.productTitle}>{productTitle}</h1>
            </div>
          )}

          {!isProductLoading && !isProductError && (
            <div className={css.calendarSectionWrapper}>
              {isTimeSlotsLoading ? (
                <div className={css.loadingInsideLayout}>
                  <div className={css.spinner} />
                  <span>Pobieranie kalendarza dostępności...</span>
                </div>
              ) : isTimeSlotsError ? (
                <div className={css.errorInsideLayout}>
                  <span>Nie udało się pobrać danych dostępności. Odśwież stronę.</span>
                </div>
              ) : (
                <div className={isTimeSlotsFetching ? css.fetchingMode : ''}>
                  <StockAvailabilityCalendar 
                    timeSlotsData={timeSlotsResponse} 
                    listingTitle={productTitle}
                    currentDate={currentDate}
                    onMonthChange={setCurrentDate}
                  />
                </div>
              )}
            </div>
          )}
        </LayoutWrapperMain>
      </LayoutSideNavigation>
    </Page>
  );
};

export default ManageStockAvailabilityPage;